import { useState, type KeyboardEvent } from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

interface TagInputProps {
  id?: string;
  values: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  inputMode?: "text" | "email";
  /** Commit-on-blur (default true) — otherwise only comma/Enter commits. */
  commitOnBlur?: boolean;
  /** Optional validator applied on commit; returns the message to reject with. */
  validate?: (v: string) => string | null;
  className?: string;
}

/**
 * Tag-style multi-input — used for destinations and invite emails.
 * Committing pushes the current draft into `values`. Backspace on an empty
 * draft pops the last tag.
 */
export function TagInput({
  id,
  values,
  onChange,
  placeholder,
  inputMode = "text",
  commitOnBlur = true,
  validate,
  className,
}: TagInputProps) {
  const [draft, setDraft] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const commit = () => {
    const v = draft.trim().replace(/,$/, "").trim();
    if (!v) return;
    if (validate) {
      const msg = validate(v);
      if (msg) {
        setErr(msg);
        return;
      }
    }
    if (values.includes(v)) {
      setErr("Already added");
      return;
    }
    onChange([...values, v]);
    setDraft("");
    setErr(null);
  };

  const remove = (v: string) => onChange(values.filter((x) => x !== v));

  const onKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      commit();
    } else if (e.key === "Backspace" && draft === "" && values.length > 0) {
      onChange(values.slice(0, -1));
    }
  };

  return (
    <div>
      <div
        className={cn(
          "flex min-h-11 flex-wrap items-center gap-1.5 rounded-md border border-line-strong",
          "bg-bg-elevated px-2 py-1.5",
          "focus-within:border-primary focus-within:shadow-[0_0_0_2px_color-mix(in_oklab,var(--primary)_25%,transparent)]",
          err && "border-danger",
          className,
        )}
      >
        {values.map((v) => (
          <span
            key={v}
            className="inline-flex items-center gap-1 rounded-full bg-primary-soft px-2.5 py-0.5 text-[13px] font-medium text-primary-ink"
          >
            {v}
            <button
              type="button"
              onClick={() => remove(v)}
              className="rounded-full p-0.5 hover:bg-primary/20"
              aria-label={`Remove ${v}`}
            >
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
        <input
          id={id}
          type={inputMode === "email" ? "email" : "text"}
          inputMode={inputMode}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            if (err) setErr(null);
          }}
          onKeyDown={onKey}
          onBlur={commitOnBlur ? commit : undefined}
          placeholder={values.length === 0 ? placeholder : ""}
          className="flex-1 min-w-[80px] bg-transparent px-1 py-1 text-[15px] text-ink outline-none placeholder:text-ink-subtle"
        />
      </div>
      {err ? (
        <p role="alert" className="mt-1 text-[12px] text-danger">
          {err}
        </p>
      ) : null}
    </div>
  );
}
